import React, { useState } from 'react';
import MDialog from '../../../components/controls/MDialog';
import { Typography, LinearProgress, Box, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper } from '@mui/material';
import RCBarChart from '../../../components/charts/recharts/rcBarChart';
import { buildQueryString, dataProvider } from '../../../DataProvider';
import { IChartDatum } from '../types';
import groupBy from 'lodash/groupBy';


/** {@link ChartView}'s dialog open/filter state, owned by the caller (`ChartButton`). */
export interface IChartViewChartState {
    /** Whether the chart dialog is open. */
    open: boolean;
    /** The filter list applied on the page when "Chart View" was clicked. */
    filterList?: Record<string, any>;
}

/** Props for {@link ChartView}. */
export interface IChartViewProps {
    /** Dialog open/filter state. */
    chart: IChartViewChartState;
    /** Setter for `chart`, used to close the dialog. */
    setChart: React.Dispatch<React.SetStateAction<IChartViewChartState>>;
}

/**
 * Dialog showing a bar chart of order assignments grouped by user
 * (`assignedTo`) and market type, fetched from `OrderAssignments/ViewChart`
 * using the caller's current filter list.
 * @param props - `chart` (`{ open, filterList }`), `setChart`.
 */
const ChartView: React.FC<IChartViewProps> = (props) => {
    const { chart, setChart } = props;
    const filter = chart.filterList;
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [data, setData] = useState<IChartDatum[]>([]);
    const [availableMarkets, setAvailableMarkets] = useState<string[]>([]);

    React.useEffect(() => {
        setIsLoading(true);
        dataProvider.fetchData('OrderAssignments', `ViewChart/Filter${convertFilter(filter as any)}`
        ).then((result: any) => {
            const converted = convertData(result.data);
            setData(converted.convertedData);
            setAvailableMarkets(converted.availableMarkets);
        }).catch((err: any) => {
            console.log(`Fail to fetch data: ${err}`, { type: 'error', autoHideDuration: 2000 });
        }).finally(() => setIsLoading(false));
    }, []);

    const convertFilter = (filter: Record<string, any>) => {
        let _filter = buildQueryString({ filter: filter }, { orderBy: 'inhandDate asc' } as any);
        return _filter;
    };

    // Data transformation: group by user (AssignedTo), count assignments per market
    const convertData = (data: any[]) => {
        let newData: IChartDatum[] = [];
        let availableMarkets: string[] = [...new Set(data.map((x: any) => x.marketType.toLowerCase().trim() as string))];
        // Filter out records with no assigned user
        data = data
            .filter((item: any) => item.assignedTo !== null && item.assignedTo !== undefined && item.assignedTo !== '');

        // Group by assignedTo (user email)
        let users = groupBy(data, 'aliasName');

        Object.keys(users).forEach(aliasName => {
            let userItems = users[aliasName];

            let dataPoint: IChartDatum = {
                name: aliasName, // Use aliasName as display name
                total: 0
            };

            // Count assignments per market for this user
            let total = 0;
            availableMarkets.forEach(market => {
                let count = userItems.filter((x: any) =>
                    x.marketType && x.marketType.toLowerCase().trim() === market
                ).length;
                dataPoint[market] = count;
                total += count;
            });
            dataPoint.total = total;
            newData.push(dataPoint);
        });
        const sortedTotal = newData.sort((a: any, b: any) => b.total - a.total);
        return { convertedData: sortedTotal, availableMarkets };
    };

    // Define series for each market with unique colors
    const getSeriesList = () => {
        const colorPalette = [
            '#E91E63', // Pink
            '#9C27B0', // Purple
            '#673AB7', // Deep Purple
            '#3F51B5', // Indigo
            '#2196F3', // Blue
            '#03A9F4', // Light Blue
            '#00BCD4', // Cyan
            '#009688', // Teal
            '#4CAF50', // Green
            '#8BC34A', // Light Green
            '#CDDC39', // Lime
            '#FFC107', // Amber
            '#FF9800', // Orange
            '#FF5722', // Deep Orange
            '#795548', // Brown
            '#9E9E9E', // Grey
            '#607D8B'  // Blue Grey
        ];

        // Generate random color (fallback if exceeding 17 markets)
        const generateRandomColor = () => {
            return '#' + Math.floor(Math.random()*16777215).toString(16).padStart(6, '0');
        };

        return availableMarkets.map((market: any, index: number) => {
            let fillColor;

            // If the market order is within 0-16 -> Take from color palette
            if (index < colorPalette.length) {
                fillColor = colorPalette[index];
            } else {
            // If market is not in predefined colors, generate random color
                fillColor = generateRandomColor();
            }

            return {
                dataKey: market,
                name: market.toUpperCase(),
                fill: fillColor,
                stackId: 'a',
                labelList: { fill: 'white' }
            };
        });
    };

    const CustomBarChartTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            const sortedPayload = payload
                .filter((item: any) => item.value > 0)
                .sort((a: any, b: any) => b.value - a.value);
            return (
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 50 }} size="small" aria-label="a dense table">
                        <TableHead>
                            <TableRow>
                                <TableCell colSpan={3}>{label}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedPayload.map((row: any) => {
                                return (
                                    <TableRow key={row.dataKey}>
                                        <TableCell align='right'>
                                            <svg height={6 * 2} width={6 * 2}>
                                                <circle cx={6} cy={6} r={6} style={{ fill: row.fill }} />
                                            </svg>
                                        </TableCell>
                                        <TableCell align="left">{row.name}</TableCell>
                                        <TableCell align="right">{row.value}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            );
        }
        return null;
    };

    return (
        <>
            <MDialog
                maxWidth={'xl'}
                open={chart.open}
                handleClose={() => setChart((prev) => ({ ...prev, open: false }))}
                name={'order-assignments-chart-dialog'}
                children={
                    <>
                        {isLoading === true && <LinearProgress color='secondary' />}
                        <Typography sx={{ marginBottom: '10px' }} variant="h3" textAlign={'center'}>Order Assignments Chart</Typography>
                        {data.length > 0 ?
                            (<RCBarChart
                                width={1500}
                                height={600}
                                data={data}
                                barSize={data.length > 18 ? 50 : ((data.length > 8) ? 70 : 90)}
                                yGap={5}
                                xAxis={{ dataKey: 'name', label: { value: 'USER', dy: 20 } }}
                                yAxis={{ position: 'insideLeft', label: { value: 'Order Assignments', angle: -90, dx: -10 } }}
                                legend={{ verticalAlign: 'top', height: 60, iconType: 'square' }}
                                showTotal={true}
                                seriesList={getSeriesList()}
                                customTooltip={CustomBarChartTooltip}
                            >
                            </RCBarChart>)
                            :
                            (
                                // Placeholder when no data is available
                                <Box
                                    sx={{
                                        height: 600,
                                        width: 1500, // Or '100%' depending on layout
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: 1
                                    }}
                                >
                                    <Typography variant="h6" color="textSecondary">
                                        No data available
                                    </Typography>
                                </Box>
                            )
                        }
                    </>
                }
            >
            </MDialog>
        </>
    );
};
export default ChartView;
